import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../../../i18n/uiText';
import {
  afterReaderSelectionSettled,
  hasReaderTextSelection,
  isVerseTextPointerTarget,
  looksLikeTextSelectionGesture,
} from '../../../utils/readerTextSelection';
import { isChapterSwipeGestureActive } from './BfHooks';
import {
  BfIconSparkle,
  BfIconBulb,
  BfIconBookOpen,
  BfIconBookmark,
  BfIconShare,
  BfIconMore,
  BfIconClose,
} from './BfIcons';

const TAP_SLOP_PX = 10;
const LONG_PRESS_MS = 360;

/**
 * BfVerse — премиальный стих без карточки/бордера.
 *
 * Поведение:
 * - Tap = onVerseTap (родитель показывает floating toolbar).
 * - Long-press = haptic + автоматически активирует стих и просит родителя
 *   развернуть полное меню (через onToggleMore).
 *
 * Совместим по пропсам с легаси VerseCard, чтобы драпать в ChapterView
 * без хирургических вмешательств.
 */
export default function BfVerse({
  verse,
  onVerseTap,
  isActive,
  isSaved = false,
  showInlineActions = false,
  onOpenInsight,
  onOpenSimple,
  onOpenApplication,
  onOpenKeyIdea,
  moreRef,
  moreOpen = false,
  onToggleMore,
  moreDirection = 'up',
  moreAlign = 'right',
  onOpenInterpretation,
  onOpenParallels,
  onShare,
  onBookmark,
  isLoadingParallel = false,
  onClose,
}) {
  const gestureRef = useRef(null);
  const longPressTimer = useRef(null);
  const gestureListenersRef = useRef(null);

  const tryHaptic = (kind = 'light') => {
    try {
      if (typeof window !== 'undefined' && window?.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred(kind);
      } else if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(kind === 'rigid' ? 12 : 6);
      }
    } catch {
      /* no-op */
    }
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const clearGestureListeners = () => {
    const listeners = gestureListenersRef.current;
    if (!listeners) return;
    window.removeEventListener('pointermove', listeners.onMove);
    window.removeEventListener('pointerup', listeners.onEnd);
    window.removeEventListener('pointercancel', listeners.onEnd);
    gestureListenersRef.current = null;
  };

  const emitVerseTap = () => {
    afterReaderSelectionSettled(() => {
      if (hasReaderTextSelection()) return;
      tryHaptic('light');
      onVerseTap?.(verse);
    });
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (hasReaderTextSelection()) return;

    const fromText = isVerseTextPointerTarget(e.target);
    clearGestureListeners();
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    gestureRef.current = {
      x: e.clientX,
      y: e.clientY,
      id: e.pointerId,
      cancelled: false,
      fromText,
      t0,
    };

    const onMove = (ev) => {
      const g = gestureRef.current;
      if (!g || ev.pointerId !== g.id) return;
      if (isChapterSwipeGestureActive()) {
        g.cancelled = true;
        return;
      }
      const dx = Math.abs(ev.clientX - g.x);
      const dy = Math.abs(ev.clientY - g.y);
      if (dx > TAP_SLOP_PX || dy > TAP_SLOP_PX) {
        g.cancelled = true;
      }
      if (hasReaderTextSelection()) {
        g.cancelled = true;
      }
    };
    const onEnd = () => {
      clearGestureListeners();
    };
    gestureListenersRef.current = { onMove, onEnd };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);

    if (!fromText) {
      clearLongPress();
      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null;
        const g = gestureRef.current;
        if (!g || g.cancelled || isChapterSwipeGestureActive() || hasReaderTextSelection()) {
          return;
        }
        tryHaptic('rigid');
        onVerseTap?.(verse);
        onToggleMore?.();
      }, LONG_PRESS_MS);
    }
  };

  const onPointerUp = (e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    clearGestureListeners();
    clearLongPress();
    if (!g || e.pointerId !== g.id) return;

    const durationMs =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) - (g.t0 || 0);
    const dx = Math.abs(e.clientX - g.x);
    const dy = Math.abs(e.clientY - g.y);

    if (
      g.cancelled ||
      isChapterSwipeGestureActive() ||
      looksLikeTextSelectionGesture(g.x, g.y, e.clientX, e.clientY, durationMs)
    ) {
      return;
    }

    if (dx <= TAP_SLOP_PX && dy <= TAP_SLOP_PX) {
      emitVerseTap();
    }
  };

  const onPointerCancel = () => {
    gestureRef.current = null;
    clearGestureListeners();
    clearLongPress();
  };

  useEffect(
    () => () => {
      clearLongPress();
      clearGestureListeners();
    },
    []
  );

  return (
    <div
      className={
        showInlineActions ? 'bf-verse-stack bf-verse-stack--open' : 'bf-verse-stack'
      }
    >
      <div
        className="bf-verse"
        data-active={isActive ? 'true' : 'false'}
        data-saved={isSaved ? 'true' : 'false'}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerCancel}
      >
        <sup
          className="bf-verse__num"
          role="button"
          tabIndex={0}
          aria-label={`${t('verse')} ${verse.verse}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onVerseTap?.(verse);
            }
          }}
        >
          {verse.verse}
        </sup>
        <span className="bf-verse__text">{verse.text}</span>
      </div>

      {showInlineActions && (
        <BfVerseToolbar
          onPrimary={onOpenInsight}
          moreRef={moreRef}
          moreOpen={moreOpen}
          onToggleMore={onToggleMore}
          moreDirection={moreDirection}
          moreAlign={moreAlign}
          onOpenKeyIdea={onOpenKeyIdea}
          onOpenInterpretation={onOpenInterpretation}
          onOpenParallels={onOpenParallels}
          onShare={onShare}
          onBookmark={onBookmark}
          isSaved={isSaved}
          isLoadingParallel={isLoadingParallel}
          onClose={onClose}
        />
      )}
    </div>
  );
}

/**
 * BfVerseToolbar — floating glass capsule с 2 первичными действиями и «...».
 * Появляется только под активным стихом (родитель управляет видимостью).
 */
function useBfVerseToolbarWebExtras() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const sync = () => {
      const root = document.documentElement;
      setShow(mq.matches && !root.hasAttribute('data-tg-webapp'));
    };
    sync();
    mq.addEventListener('change', sync);
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-tg-webapp'],
    });
    return () => {
      mq.removeEventListener('change', sync);
      obs.disconnect();
    };
  }, []);

  return show;
}

function BfVerseToolbar({
  onPrimary,
  moreRef,
  moreOpen,
  onToggleMore,
  onOpenKeyIdea,
  onOpenInterpretation,
  onOpenParallels,
  onShare,
  onBookmark,
  isSaved = false,
  isLoadingParallel,
  onClose,
}) {
  const moreBtnRef = useRef(null);
  const [popPos, setPopPos] = useState(null);
  const showWebExtras = useBfVerseToolbarWebExtras();

  useLayoutEffect(() => {
    if (!moreOpen) {
      setPopPos(null);
      return undefined;
    }
    const calc = () => {
      const btn = moreBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const POP_W = 220;
      const POP_H = 280; // оценочно
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // По вертикали: предпочитаем выше кнопки, если влезает, иначе ниже.
      const spaceAbove = r.top - margin;
      const placeAbove = spaceAbove >= POP_H || spaceAbove > vh - r.bottom - margin;
      const top = placeAbove ? Math.max(margin, r.top - 6) : Math.min(vh - margin, r.bottom + 6);
      // По горизонтали: правый край выравниваем по правому краю кнопки, но не вылезаем за окно.
      let right = vw - r.right;
      if (right < margin) right = margin;
      if (vw - right - POP_W < margin) right = Math.max(margin, vw - POP_W - margin);
      setPopPos({ top, right, placeAbove });
    };
    calc();
    window.addEventListener('resize', calc);
    window.addEventListener('scroll', calc, true);
    return () => {
      window.removeEventListener('resize', calc);
      window.removeEventListener('scroll', calc, true);
    };
  }, [moreOpen]);

  return (
    <div
      className="bf-verse-toolbar"
      role="group"
      aria-label={t('verseActions')}
    >
      <button
        type="button"
        className="bf-verse-toolbar__btn bf-verse-toolbar__btn--primary"
        onClick={(e) => {
          e.stopPropagation();
          onPrimary?.();
        }}
      >
        <span className="bf-verse-toolbar__spark" aria-hidden="true">
          {'\u{1F4A1}'}
        </span>
        <span>{t('verseInsightButton')}</span>
      </button>
      {onOpenParallels && (
        <button
          type="button"
          className="bf-verse-toolbar__btn bf-verse-toolbar__btn--ghost"
          disabled={isLoadingParallel}
          onClick={(e) => {
            e.stopPropagation();
            onOpenParallels();
          }}
        >
          <BfIconBookOpen /> {t('parallels')}
        </button>
      )}
      {showWebExtras && (
        <div className="bf-verse-toolbar__web-extra">
          {onOpenInterpretation && (
            <button
              type="button"
              className="bf-verse-toolbar__btn bf-verse-toolbar__btn--ghost"
              onClick={(e) => {
                e.stopPropagation();
                onOpenInterpretation();
              }}
            >
              {t('interpretation')}
            </button>
          )}
        </div>
      )}
      <div
        ref={moreRef}
        className="bf-verse-toolbar__more-wrap"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={moreBtnRef}
          type="button"
          className="bf-verse-toolbar__more"
          aria-expanded={moreOpen}
          aria-label={t('moreActions')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleMore?.();
          }}
        >
          <BfIconMore />
        </button>
        {moreOpen && popPos && typeof document !== 'undefined' &&
          createPortal(
            <div
              className="bf-popover bf-popover--fixed"
              role="menu"
              style={{
                position: 'fixed',
                top: popPos.placeAbove ? 'auto' : popPos.top,
                bottom: popPos.placeAbove ? `calc(100vh - ${popPos.top}px)` : 'auto',
                right: popPos.right,
                left: 'auto',
                minWidth: 220,
                maxWidth: 280,
                zIndex: 80,
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="bf-popover__item"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenKeyIdea?.();
                }}
              >
                <BfIconBulb /> {t('chatFollowUpKeyThought')}
              </button>
              {!showWebExtras && (
                <button
                  type="button"
                  className="bf-popover__item"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenInterpretation?.();
                  }}
                >
                  <BfIconSparkle /> {t('interpretation')}
                </button>
              )}
              {onBookmark && (
                <button
                  type="button"
                  className="bf-popover__item"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBookmark?.();
                  }}
                >
                  <BfIconBookmark /> {isSaved ? t('unsaveVerse') : t('saveVerse')}
                </button>
              )}
              <button
                type="button"
                className="bf-popover__item"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare?.();
                }}
              >
                <BfIconShare /> {t('share')}
              </button>
            </div>,
            document.body
          )}
      </div>
      {onClose && (
        <div className="bf-verse-toolbar__close-wrap">
          <button
            type="button"
            className="bf-verse-toolbar__close"
            aria-label={t('close')}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            <BfIconClose />
          </button>
        </div>
      )}
    </div>
  );
}
